import useTranslation from 'next-translate/useTranslation'
import { FC, useState, useMemo } from 'react'
import { Translate } from 'next-translate'
import {
  baseValidator,
  newEmailValidator,
  phoneValidator
} from '@/core/validationUtils'
import { Box, Grid } from '@/components/atoms/Grid'
import styled, { useTheme } from 'styled-components'
import { BlackButton } from '@/components/atoms/button'
import {
  getValidationResult,
  initData,
  isFormValid,
  mapInputsToValues,
  onInputChange
} from '@/components/molecules/form/form'
import { Input, InputValue } from '@/components/molecules/form/form'
import FormElement from '@/components/molecules/form/formElement'
import FormInput, { FormInputType } from '@/components/molecules/form/formInput'
import { SelectOptions } from '../molecules/selectOptions'
import { v4 as uuidv4 } from 'uuid'

const makeFirstNameField = (t: Translate): Input => ({
  title: t('Contact.Form.firstname'),
  name: 'firstname',
  rules: [
    {
      isValid: baseValidator,
      errorMessage: t('Contact.Form.emptyFirstnameErrorPlaceholder')
    }
  ]
})

const makeLastNameField = (t: Translate): Input => ({
  title: t('Contact.Form.lastname'),
  name: 'lastname',
  rules: [
    {
      isValid: baseValidator,
      errorMessage: t('Contact.Form.emptyLastnameErrorPlaceholder')
    }
  ]
})

const makePhoneField = (t: Translate): Input => ({
  title: t('Contact.Form.phone'),
  name: 'phone'
})

const makeRequiredPhoneField = (
  t: Translate,
  countryIsoCode: string
): Input => ({
  title: t('Contact.Form.phone'),
  name: 'phone',
  rules: [
    {
      isValid: baseValidator,
      errorMessage: t('Contact.Form.emptyPhoneErrorPlaceholder')
    },
    {
      isValid: (value) => phoneValidator(countryIsoCode, value),
      errorMessage: t('Contact.Form.notValidPhoneErrorPlaceholder')
    }
  ]
})

const makeEmailField = (t: Translate): Input => ({
  title: t('Contact.Form.email'),
  name: 'email',
  rules: [
    {
      isValid: baseValidator,
      errorMessage: t('Contact.Form.emptyEmailErrorPlaceholder')
    },
    {
      isValid: newEmailValidator,
      errorMessage: t('Contact.Form.notValidEmailErrorPlaceholder')
    }
  ]
})

const makeMessageField = (t: Translate): Input => ({
  title: t('Contact.Form.message'),
  name: 'message',
  rules: [
    {
      isValid: baseValidator,
      errorMessage: t('Contact.Form.emptyMessageErrorPlaceholder')
    }
  ],
  type: FormInputType.TextArea,
  gridColumn: { desktop: '1 / 3', tablet: '1 / 3', mobile: '1 / 2' }
})

const makeCvField = (t: Translate): Input => ({
  title: '',
  name: 'cv',
  placeholder: t('Contact.Form.cv'),
  rules: [
    {
      isValid: baseValidator,
      errorMessage: t('Contact.Form.emptyCvErrorPlaceholder')
    }
  ],
  type: FormInputType.File
})

export const makeB2bFormFields = (t: Translate): Input[] => [
  makeFirstNameField(t),
  makeLastNameField(t),
  makePhoneField(t),
  makeEmailField(t),
  makeMessageField(t)
]

export const makeHiringFormFields = (
  t: Translate,
  countryIsoCode: string
): Input[] => [
  makeFirstNameField(t),
  makeLastNameField(t),
  makeEmailField(t),
  makeRequiredPhoneField(t, countryIsoCode),
  makeCvField(t)
]

const useCreateSubjectOptions = () => {
  const { t } = useTranslation('common')

  const subjectValues = [
    t('Contact.Form.SelectOptions.order'),
    t('Contact.Form.SelectOptions.return'),
    t('Contact.Form.SelectOptions.payment'),
    t('Contact.Form.SelectOptions.complaint'),
    t('Contact.Form.SelectOptions.registration'),
    t('Contact.Form.SelectOptions.other')
  ]

  const subjectKeys = subjectValues.map(() => useMemo(() => uuidv4(), []))

  return subjectValues.map((subject, idx) => ({
    id: subjectKeys[idx],
    value: subject
  }))
}

type ContactFormProps = {
  formFields: Input[]
  onSend: (
    data: Record<string, InputValue>,
    files: Record<string, FileList>,
    clearData: () => void
  ) => Promise<void>
} & Rest

const ContactForm: FC<ContactFormProps> = ({
  formFields,
  onSend,
  children,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const theme = useTheme()
  const [showValidation, setShowValidation] = useState<boolean>(false)
  const [data, setData] = useState<Record<string, InputValue>>(() =>
    mapInputsToValues(formFields)
  )

  const [files, setFiles] = useState<Record<string, FileList>>({})

  const validate = (): boolean => {
    setShowValidation(true)

    return (
      isFormValid(data) &&
      (!formFields.find(({ name }) => name === 'cv') || validateCv())
    )
  }

  const validateCv = (): boolean => {
    const setError = (errorMessage) => {
      setData((data) => ({
        ...data,
        ...{
          cv: { isValid: false, errorMessage, value: data?.cv?.value }
        }
      }))
    }
    const file = files?.cv?.[0]
    if (!file) {
      setError(t('Contact.Form.emptyCvErrorPlaceholder'))
      return false
    }
    const allowedExtensionsRegx = /(\.pdf|\.docx|\.doc)$/i
    const extension = file.name.substring(file.name.lastIndexOf('.'))
    if (!allowedExtensionsRegx.test(extension)) {
      setError(t('Contact.Form.notValidCvExtErrorPlaceholder'))
      return false
    }

    const allowedTypesRegx =
      /(application\/pdf|application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document|\.application\/msword)$/i
    if (!allowedTypesRegx.test(file.type)) {
      setError(t('Contact.Form.notValidCvExtErrorPlaceholder'))
      return false
    }

    const size = Math.round(file.size / 1024)
    if (size > 5 * 1024) {
      setError(t('Contact.Form.notValidCvSizeErrorPlaceholder'))
      return false
    }

    return true
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    validate() && (await send())
  }

  const send = async () => {
    await onSend(data, files, () => {
      setShowValidation(false)
      initData(formFields, setData)
    })
  }

  const selectOptions = useCreateSubjectOptions()

  return (
    <Box backgroundColor={theme.colors.tableBackground} p={3}>
      <Grid
        gridTemplateColumns={{
          desktop: '1fr 1fr',
          tablet: '1fr 1fr',
          mobile: '1fr'
        }}
        gridRowGap={'16px'}
        gridColumnGap={'16px'}
        {...rest}
      >
        {formFields.map(
          ({ name, title, placeholder, rules, type, ...rest }) => {
            const { isValid } = getValidationResult(data[name], rules)
            return (
              <Box key={name} {...rest}>
                <FormElement showValidation={showValidation} isValid={isValid}>
                  {name !== 'subject' ? (
                    <FormInput
                      name={name}
                      value={data[name]?.value}
                      placeholder={title || placeholder}
                      onChange={(name, value) =>
                        onInputChange(name, value, rules, setData)
                      }
                      onFileChange={(name, value) => {
                        setFiles((record) => ({
                          ...record,
                          [name]: value
                        }))
                      }}
                      formInputType={type}
                      isError={showValidation && !isValid}
                      adjustPlaceholderPosition
                    />
                  ) : (
                    <SelectOptions
                      options={selectOptions}
                      placeholder={title || placeholder}
                      isError={showValidation && !isValid}
                      selectedItem={data[name]}
                      handleSelect={(option) =>
                        onInputChange(name, option?.value, rules, setData)
                      }
                      adjustPlaceholderPosition
                    />
                  )}
                </FormElement>
              </Box>
            )
          }
        )}
      </Grid>
      {children}
      <Box textAlign="center">
        <SubmitButton title={t('Contact.Form.submit')} onClick={onSubmit} />
      </Box>
    </Box>
  )
}

export default ContactForm

const SubmitButton = styled(BlackButton)`
  text-transform: uppercase;
`
